/* eslint-disable no-extra-boolean-cast */
import classNames from 'classnames'
import { Icon } from 'components/common/Icon'
import { Info } from 'components/common/Info'
import { Separator } from 'components/common/Separator'
import { TradeButton } from 'components/common/TradeButton'
import { useWaysTo } from 'components/common/ways-to/WaysTo'
import { Currencies } from 'constants/currencies'
import { useEditionPrices } from 'hooks/useEditionPrices'
import { useExistingTrade } from 'hooks/useExistingTrade'
import { useHowItWorksModal } from 'hooks/useHowItWorksModal'
import throttle from 'lodash/throttle'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { ContainerElement, TradeFlowType } from 'types'
import { Artist, Edition } from 'utils/data'
import { getArtworkUrlFromRoot } from 'utils/urls'
import { useCurrencies, useOverlay } from '../../contexts'

type IsLoadingType = 'bidOrBuy' | 'bid' | 'askOrSell' | 'ask'

type IsLoadingInterface = {
  [key in IsLoadingType]: boolean
}

type Props = {
  currentEdition?: Edition | null
  artists: Artist[]
  artwork: {
    id: number
    name: string
  }
} & ContainerElement

export const ArtworkTrade = ({
  currentEdition,
  artists,
  artwork,
  children,
}: Props) => {
  const { t } = useTranslation('common')
  const [isLoading, setIsLoading] = useState<IsLoadingInterface>({
    bidOrBuy: false,
    bid: false,
    askOrSell: false,
    ask: false,
  })
  const [perspective, setPerspective] = useState<'buying' | 'selling'>('buying')
  const [isPinned, setIsPinned] = useState(false)
  const setWaysTo = useWaysTo()
  const { setIsOpen } = useOverlay()
  const { maybeShow } = useHowItWorksModal()
  const { formatCurrency } = useCurrencies()
  const router = useRouter()

  const buttonsContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onScroll = throttle(() => {
      if (!buttonsContainerRef.current) return

      const { top } = buttonsContainerRef.current.getBoundingClientRect()

      setIsPinned(top <= 64 && top >= 0)
    }, 150)

    window.addEventListener('scroll', onScroll)

    return () => {
      setIsOpen(false)
      window.removeEventListener('scroll', onScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setIsLoadingHandler = useCallback(
    (type: IsLoadingType) =>
      setIsLoading(prevState => ({ ...prevState, [type]: true })),
    []
  )

  const clickHandler = (
    e: MouseEvent<HTMLAnchorElement>,
    type: keyof IsLoadingInterface,
    href: string
  ) => {
    if (e.isDefaultPrevented()) return

    const isBidOrBuy = type === 'bid' || type === 'bidOrBuy'
    const tradeType: TradeFlowType = isBidOrBuy ? 'bid-or-buy' : 'ask-or-sell'

    const isShowing = maybeShow(tradeType, href)
    if (isShowing) {
      e.preventDefault()
      return
    }

    setIsLoadingHandler(type)
  }

  function onSell() {
    if (highestBid) {
      setPerspective('selling')
      return
    }

    if (maybeShow('ask-or-sell', askPath)) {
      return
    }

    router.push(askPath)
  }

  function onBuy() {
    if (lowestAsk) {
      setPerspective('buying')
      return
    }

    if (maybeShow('bid-or-buy', buyPath)) {
      return
    }

    router.push(buyPath)
  }

  const getNextStepPath = useCallback(
    (root: string) =>
      getArtworkUrlFromRoot(root, artists, artwork.name, artwork.id) +
      `/${currentEdition?.name}`,
    [artists, artwork.id, artwork.name, currentEdition?.name]
  )

  const existingTradePayload = {
    currentEdition,
    artists,
    name: artwork.name,
    id: artwork.id,
  }

  const { editUrl: editAskUrl } = useExistingTrade({
    ...existingTradePayload,
    tradeType: 'ask',
  })
  const { editUrl: editBidUrl } = useExistingTrade({
    ...existingTradePayload,
    tradeType: 'bid',
  })

  const bidOrBuyPath = getNextStepPath('bid-or-buy')
  const buyPath = bidOrBuyPath + '?action=buy'
  const bidPath = editBidUrl || bidOrBuyPath + '?action=bid'

  const askOrSellPath = getNextStepPath('ask-or-sell')
  const sellPath = askOrSellPath + '?action=sell'
  const askPath = editAskUrl || askOrSellPath + '?action=ask'

  const { highestBid, lowestAsk } = useEditionPrices({
    edition: currentEdition,
  })

  const hasValues =
    typeof lowestAsk !== 'undefined' && typeof highestBid !== 'undefined'

  return (
    <div className='contents rounded border-cool-300 bg-cool-50 lg:block lg:border lg:p-6 lg:pb-5'>
      {children}

      {!hasValues && <Skeleton width='100%' height={48} borderRadius={8} />}

      {hasValues && (
        <>
          <div
            ref={buttonsContainerRef}
            className={classNames(
              'sticky top-16 z-10 -mx-6 w-[calc(100%+3rem)] bg-cool-50 p-6 pb-4 pt-6 sm:-mx-12 sm:w-[calc(100%+6rem)] sm:px-12 lg:static lg:mx-0 lg:w-full lg:bg-transparent lg:p-0 lg:shadow-none',
              'flex flex-col space-y-4 lg:space-y-5',
              isPinned && 'shadow'
            )}
          >
            {perspective === 'buying' && (
              <>
                {!lowestAsk && currentEdition && (
                  <Info>
                    <Trans
                      i18nKey='single_artwork:this_work_isnt_listed_right_now'
                      components={{
                        a: (
                          <button
                            className='text-secondary-default underline'
                            onClick={() =>
                              setWaysTo({
                                type: 'buy',
                                edition: currentEdition,
                                artists,
                                artwork,
                              })
                            }
                          />
                        ),
                      }}
                    />
                  </Info>
                )}

                {Boolean(lowestAsk) ? (
                  <div className='grid grid-cols-2 gap-x-4'>
                    <TradeButton
                      data-e2e='buy-button'
                      theme='green'
                      href={buyPath}
                      value={lowestAsk}
                      isLoading={isLoading.bidOrBuy}
                      onClick={e => clickHandler(e, 'bidOrBuy', buyPath)}
                    >
                      {t('buy_now')}
                    </TradeButton>
                    <TradeButton
                      data-e2e='bid-button'
                      theme='outlineGreen'
                      href={bidPath}
                      isLoading={isLoading.bid}
                      onClick={e => clickHandler(e, 'bid', bidPath)}
                    >
                      {t('make_offer')}
                    </TradeButton>
                  </div>
                ) : (
                  <TradeButton
                    data-e2e='bid-button'
                    theme='green'
                    href={bidPath}
                    isLoading={isLoading.bid}
                    onClick={e => clickHandler(e, 'bid', bidPath)}
                  >
                    {t('make_offer')}
                  </TradeButton>
                )}

                <Separator />

                <button
                  type='button'
                  className='w-full whitespace-pre-line rounded bg-purple-50/80 px-1 py-3 text-center text-sm'
                  onClick={onSell}
                >
                  {!highestBid && (
                    <Trans
                      i18nKey='single_artwork:looking_to_sell_this_work'
                      components={{
                        list: <span className='font-bold text-purple-500' />,
                        arrow: (
                          <Icon
                            name='forward-arrow'
                            size={20}
                            className='!inline-flex align-middle text-purple-500'
                          />
                        ),
                      }}
                    />
                  )}

                  {Boolean(highestBid) && (
                    <>
                      <span className='block lg:inline'>
                        {t('single_artwork:looking_to_sell')}
                      </span>
                      <Trans
                        i18nKey='single_artwork:sell_now_or_list'
                        values={{
                          price: formatCurrency(highestBid, {
                            from: Currencies.USER,
                            to: Currencies.USER,
                          }),
                        }}
                        components={{
                          sell: (
                            <span className='mx-1 whitespace-pre-line font-bold text-purple-500' />
                          ),
                          list: (
                            <span className='ml-1 font-medium text-purple-500' />
                          ),
                          arrow: (
                            <Icon
                              name='forward-arrow'
                              size={20}
                              className='!inline-flex align-middle text-purple-500'
                            />
                          ),
                        }}
                      />
                    </>
                  )}
                </button>
              </>
            )}

            {perspective === 'selling' && (
              <>
                {!lowestAsk && Boolean(highestBid) && (
                  <Info>
                    <Trans
                      i18nKey='single_artwork:an_interested_collector_placed_an_offer'
                      values={{
                        price: formatCurrency(highestBid, {
                          from: Currencies.USER,
                          to: Currencies.USER,
                        }),
                      }}
                      components={{
                        b: <b />,
                        sell: (
                          <Link
                            href={sellPath}
                            className='text-secondary-default underline'
                          />
                        ),
                        list: (
                          <Link
                            href={askPath}
                            className='text-secondary-default underline'
                          />
                        ),
                      }}
                    />
                  </Info>
                )}

                {Boolean(highestBid) ? (
                  <div className='grid grid-cols-2 gap-x-4'>
                    <TradeButton
                      data-e2e='sell-button'
                      theme='gray'
                      href={sellPath}
                      value={highestBid}
                      isLoading={isLoading.askOrSell}
                      onClick={e => clickHandler(e, 'askOrSell', sellPath)}
                    >
                      {t('sell_now')}
                    </TradeButton>
                    <TradeButton
                      data-e2e='ask-button'
                      theme='outlineGray'
                      href={askPath}
                      isLoading={isLoading.ask}
                      onClick={e => clickHandler(e, 'ask', askPath)}
                    >
                      {t('list_for_more')}
                    </TradeButton>
                  </div>
                ) : (
                  <TradeButton
                    data-e2e='ask-button'
                    theme='red'
                    href={askPath}
                    isLoading={isLoading.ask}
                    onClick={e => clickHandler(e, 'ask', askPath)}
                  >
                    {t('list_for_sale')}
                  </TradeButton>
                )}

                <Separator />

                <button
                  type='button'
                  className='w-full whitespace-pre-line rounded bg-purple-50/80 px-1 py-3 text-center text-sm'
                  onClick={onBuy}
                >
                  {!lowestAsk && (
                    <Trans
                      i18nKey='single_artwork:looking_to_buy_this_work'
                      components={{
                        makeOffer: (
                          <span className='font-bold text-purple-500' />
                        ),
                        arrow: (
                          <Icon
                            name='forward-arrow'
                            size={20}
                            className='!inline-flex align-middle text-purple-500'
                          />
                        ),
                      }}
                    />
                  )}

                  {Boolean(lowestAsk) && (
                    <>
                      <span className='block lg:inline'>
                        {t('single_artwork:looking_to_buy')}
                      </span>
                      <Trans
                        i18nKey='single_artwork:buy_now_or_make_offer'
                        values={{
                          price: formatCurrency(lowestAsk, {
                            from: Currencies.USER,
                            to: Currencies.USER,
                          }),
                        }}
                        components={{
                          buy: (
                            <span className='mx-1 whitespace-pre-line font-bold text-purple-500' />
                          ),
                          makeOffer: (
                            <span className='ml-1 font-medium text-purple-500' />
                          ),
                          arrow: (
                            <Icon
                              name='forward-arrow'
                              size={20}
                              className='!inline-flex align-middle text-purple-500'
                            />
                          ),
                        }}
                      />
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}
